/**
 * PATH: packages/email-hub/src/feature/referrals/SuggestedOrgChip.tsx
 * FILE_ID: EMAILHUB-SUGGESTED-ORG-CHIP-001
 * PURPOSE: Confirm/dismiss a non-applying organisation suggestion.
 * WHY: Suggestions explain themselves and never transfer without a click.
 * NOTES FOR FUTURE AI: Multi-org chrome only. "Move this somewhere else" is an
 *   unanswerable question for someone with one organisation, so this chip is
 *   gated on membership count exactly like ReferToOrgAction. Change #328 gated
 *   the suggested_org REVIEW ROW and missed this chip, which renders the same
 *   suggestion in inbox rows and the FiledTo lane; #333 closed that leak. If
 *   you add a third surface for suggested_org, gate it here too.
 */
import { Check, Loader2, Sparkles, X } from 'lucide-react'

import { useToast } from '../../_internal/components/Toast'
import type { SuggestedOrg } from '../hooks/useEmails'
import { usePersonalMembershipCount } from '../personal/usePersonalData'
import { useOrgSuggestionDecision } from './useReferralData'

interface SuggestedOrgChipProps {
  emailId: string
  suggested: SuggestedOrg | null | undefined
  onChanged?: () => void
}

export function SuggestedOrgChip({
  emailId,
  suggested,
  onChanged,
}: SuggestedOrgChipProps) {
  const membershipCount = usePersonalMembershipCount()
  const decision = useOrgSuggestionDecision()
  const { toast } = useToast()

  // Undefined while the count is still loading, so this also suppresses a
  // flash of cross-org chrome on first paint rather than showing then hiding.
  if (!suggested || (membershipCount.data ?? 0) < 2) return null

  const decide = async (next: 'confirm' | 'dismiss') => {
    try {
      await decision.mutateAsync({
        emailId,
        targetOrgId: suggested.org_id,
        decision: next,
      })
      toast(
        next === 'confirm'
          ? `Referred to ${suggested.org_name}`
          : `Dismissed ${suggested.org_name} suggestion`,
        'success',
      )
      onChanged?.()
    } catch (error) {
      toast(
        error instanceof Error
          ? error.message
          : 'The organisation suggestion could not be updated.',
        'error',
      )
    }
  }

  const confidence = Math.round(suggested.confidence * 100)
  return (
    <div
      role="group"
      aria-label={`Suggested organisation ${suggested.org_name}`}
      title={`${suggested.reason} (${confidence}% confidence)`}
      onClick={(event) => event.stopPropagation()}
      className="inline-flex max-w-[280px] items-stretch overflow-hidden rounded-full border border-dashed border-violet-400/70 bg-violet-50 text-violet-800 dark:border-violet-500/40 dark:bg-violet-900/20 dark:text-violet-200"
    >
      <span className="inline-flex min-w-0 items-center gap-1 py-0.5 pl-2 pr-1.5 text-[10px] font-medium">
        <Sparkles className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">AI: {suggested.org_name}</span>
        <span className="shrink-0 opacity-70">{confidence}%</span>
      </span>
      <button
        type="button"
        disabled={decision.isPending}
        onClick={(event) => {
          event.stopPropagation()
          void decide('confirm')
        }}
        aria-label={`Confirm ${suggested.org_name} organisation suggestion`}
        className="inline-flex items-center justify-center border-l border-violet-400/50 px-2 text-emerald-600 hover:bg-emerald-500/20 disabled:opacity-50 dark:text-emerald-300"
      >
        {decision.isPending && decision.variables?.decision === 'confirm'
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <Check className="h-3 w-3" />}
      </button>
      <button
        type="button"
        disabled={decision.isPending}
        onClick={(event) => {
          event.stopPropagation()
          void decide('dismiss')
        }}
        aria-label={`Dismiss ${suggested.org_name} organisation suggestion`}
        className="inline-flex items-center justify-center border-l border-violet-400/50 px-2 text-muted-foreground hover:bg-red-500/15 hover:text-red-600 disabled:opacity-50"
      >
        {decision.isPending && decision.variables?.decision === 'dismiss'
          ? <Loader2 className="h-3 w-3 animate-spin" />
          : <X className="h-3 w-3" />}
      </button>
    </div>
  )
}
